<template>
  <v-container>
    <v-toolbar flat color="toolbar-action transparent">
      <v-menu
        ref="menu"
        v-model="menu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        min-width="auto"
        z-index="20"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            autocomplete="off"
            v-model="search.keyword"
            label=""
            placeholder="Search Groups"
            prepend-inner-icon="mdi-magnify"
            filled
            rounded
            dense
            clearable
            v-bind="attrs"
            v-on="on"
            class="v-input--search"
            @click:clear="clearSearchAction"
            @keydown.enter="searchAction()"
          ></v-text-field>
        </template>
        <GroupSearchForm
          @clicked-search-button="clickedSearchButtonInModal"
          @clicked-clear-button="clearSearchAction"
          :meta="meta"
          :initialValues="getInitialFormValues()"
        />
      </v-menu>
      <v-spacer></v-spacer>
      <v-btn color="primary v-btn--new" dark class="ma-2" to="/groups/new">New</v-btn>
    </v-toolbar>
    <Paginate :meta="meta" v-on:paginate="paginateAction" />
    <v-data-table
      :headers="headers"
      :items="groups"
      :options.sync="options"
      @click:row="editItem"
      :loading="loading"
      loading-text="Loading... Please wait"
      hide-default-footer
    >
      <template v-slot:item.subgroups="{ item }">
        <span v-for="subgroup in item.subgroups" :key="subgroup.id">
          <router-link :to="`/subgroups/${subgroup.id}`" @click.stop class="base-link subgroup-link">{{
            subgroup.title
          }}</router-link>
        </span>
      </template>

      <template v-slot:item.start_date="{ item }">
        {{ formatDate(item.start_date) }}
      </template>

      <template v-slot:item.end_date="{ item }">
        {{ formatDate(item.end_date) }}
      </template>

      <template v-slot:item.actions="{ item }">
        <v-spacer></v-spacer>
        <v-icon small @click.stop="deleteItem(item)"> mdi-delete </v-icon>
      </template>
    </v-data-table>
    <ConfirmDialog
      title="Confirm delete"
      description="Are you sure you want to delete this item"
      okayLabel="Yes"
      cancelLabel="No"
      :show="showDeleteConfirmDialog"
      @confirm-dialog-clicked="confirmDelete"
    />
  </v-container>
</template>
<style scoped>
.v-input--search {
  max-width: 80%;
}
.v-btn--new {
  position: absolute;
  top: -12px;
  right: 0;
}
.v-menu__content {
  max-width: 640px;
  padding: 20px 20px 0 20px;
  background: white;
}
</style>
<script>
import { dateMixin } from '@/mixins/dateMixin';
import { mapGetters } from 'vuex';
import store from '@/store/index';
import ConfirmDialog from '@/views/dialogs/ConfirmDialog';
import GroupSearchForm from '@/views/GroupSearchForm';
import Paginate from '@/views/Paginate';

export default {
  name: 'GroupsView',
  components: {
    ConfirmDialog,
    GroupSearchForm,
    Paginate
  },
  mixins: [dateMixin],
  beforeRouteEnter(to, from, next) {
    const currentPage = parseInt(to.query.page) || 1;
    const sortBy = to.query.sort_by || 'id';
    const sortDesc = to.query.sort_desc === undefined ? true : to.query.sort_desc === 'true';
    const keyword = to.query.keyword || '';
    const active = to.query.active === 'true';
    if (keyword || active) {
      let searchParams = {
        page: currentPage,
        sort_by: sortBy,
        sort_desc: sortDesc
      };
      if (keyword) {
        searchParams.keyword = to.query.keyword;
      } else {
        if (to.query.title) {
          searchParams.title = to.query.title;
        }
        if (to.query.start_date) {
          searchParams.start_date = to.query.start_date;
        }
        if (to.query.end_date) {
          searchParams.end_date = to.query.end_date;
        }
        if (to.query.group_statuses_id) {
          searchParams.group_statuses_id = to.query.group_statuses_id;
        }
        if (to.query.group_types_id) {
          searchParams.group_types_id = to.query.group_types_id;
        }
        if (to.query.branch_id) {
          searchParams.branch_id = to.query.branch_id;
        }
      }
      store.dispatch('group/search', searchParams).then(() => {
        to.params.page = currentPage;
        next(vm => {
          if (keyword) {
            vm.search.keyword = keyword;
            vm.search.active = true;
          }
        });
      });
    } else {
      // Browse default groups
      store
        .dispatch('group/list', {
          page: currentPage,
          sort_by: sortBy,
          sort_desc: sortDesc
        })
        .then(() => {
          to.params.page = currentPage;
          next();
        });
    }
  },
  methods: {
    load: function (updateQuery = true) {
      const options = {
        page: this.options.page,
        sort_by: this.options.sortBy[0] ?? 'id',
        sort_desc: this.options.sortDesc[0] ?? false
      };
      if (updateQuery) {
        this.$router.push({
          query: options
        });
      }
      store.dispatch('group/list', options);
    },
    editItem(item) {
      this.$router.push({ path: `/groups/${item.id}` });
    },
    deleteItem(item) {
      this.group_id = item.id;
      this.showDeleteConfirmDialog = true;
    },
    confirmDelete: function (deleteItem) {
      this.showDeleteConfirmDialog = false;
      if (deleteItem) {
        this.processDelete();
      }
    },
    async processDelete() {
      await store.dispatch('group/delete', this.group_id);
      this.options.page = parseInt(this.$route.query.page) || 1;
      this.load(false);
    },
    paginateAction(item, page) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: page
        }
      });
      if (item == 'next') {
        store.dispatch('group/paginate', this.links.next);
      } else if (item == 'previous') {
        store.dispatch('group/paginate', this.links.prev);
      }
    },
    clickedSearchButtonInModal(options) {
      this.menu = false;
      const defaultOptions = {
        active: true,
        page: this.options.page,
        sort_by: this.options.sortBy[0] ?? 'id',
        sort_desc: this.options.sortDesc[0] ?? false
      };
      this.searchQuery({
        ...defaultOptions,
        ...options
      });
    },
    searchAction() {
      this.searchQuery({
        keyword: this.search.keyword,
        active: true,
        page: this.options.page,
        sort_by: this.options.sortBy[0] ?? 'id',
        sort_desc: this.options.sortDesc[0] ?? false
      });
    },
    searchQuery(options) {
      Object.keys(options).forEach(key => {
        if (options[key] === null || options[key] === undefined) {
          delete options[key];
        }
      });
      store.dispatch('group/search', options);
      this.$router.push({
        query: options
      });
      this.search = options;
      this.menu = false;
    },
    clearSearchAction() {
      this.search = {
        keyword: '',
        active: false
      };
      this.$router.push({
        query: {}
      });
      this.load(false);
    },
    getInitialFormValues() {
      const query = this.$route.query;
      return {
        title: query.title || null,
        group_statuses_id: query.group_statuses_id ? parseInt(query.group_statuses_id) : null,
        group_types_id: query.group_types_id ? parseInt(query.group_types_id) : null,
        start_date: query.start_date || null,
        end_date: query.end_date || null,
        branch_id: query.branch_id ? parseInt(query.branch_id) : null
      };
    }
  },
  computed: {
    ...mapGetters('group', ['loading', 'error', 'groups', 'meta', 'links'])
  },
  watch: {
    options: {
      handler() {
        if (this.options.init) {
          if (this.search.active) {
            this.search.sort_by = this.options.sortBy[0] ?? 'id';
            this.search.sort_desc = this.options.sortDesc[0] ?? false;
            this.searchQuery(this.search, true);
          } else {
            this.load();
          }
        } else {
          this.options.init = true;
        }
      },
      deep: true
    }
  },
  data: () => ({
    menu: false,
    search: {
      keyword: '',
      active: false
    },
    showDeleteConfirmDialog: false,
    options: {},
    headers: [
      { text: 'ID#', value: 'id' },
      { text: 'Group Name', value: 'title' },
      { text: 'Status', value: 'status' },
      { text: 'Subgroup', value: 'subgroups', sortable: false },
      { text: 'Client Name', value: 'client_name' },
      { text: 'Client #', value: 'client_reference' },
      { text: 'Start Date', value: 'start_date' },
      { text: 'End Date', value: 'end_date' },
      { text: 'Actions', value: 'actions', sortable: false, align: 'right' }
    ]
  })
};
</script>
